import {
  ACTIVE_COLUMNS, CLOSED_COLUMN_LABEL, CLOSED_COLUMNS, RECENT_COMPLETED_DAYS, TABLERO_COLUMN_LABEL, TABLERO_COLUMNS, type BoardCountsDTO, type BoardScope, type ClosedResult, type TableroColumn,
} from "@bgreen/shared";
import { CheckCircle2, ChevronLeft, ChevronRight, Loader2 } from "lucide-react";
import { useCallback, useEffect, useRef, useState, type RefObject } from "react";
import { cn } from "../../lib/cn";
import { useBoardPages } from "../../lib/board";
import { Button } from "../ui/button";
import { Skeleton } from "../ui/primitives";
import { BoardCard } from "./BoardCard";

/** Una columna del tablero: una fase de trabajo, «Completadas recientes» (Tablero) o un resultado de cierre (Cerradas). */
export type ColumnKey = TableroColumn | ClosedResult;
const isResult = (k: ColumnKey): k is ClosedResult => (CLOSED_COLUMNS as readonly string[]).includes(k);

/** Columnas de cada ámbito, en orden. «Necesita mi intervención» solo tiene las fases de trabajo: nunca completadas. */
export const columnsFor = (scope: BoardScope): readonly ColumnKey[] => (scope === "closed" ? CLOSED_COLUMNS : scope === "attention" ? ACTIVE_COLUMNS : TABLERO_COLUMNS);
export const columnLabel = (k: ColumnKey) => (isResult(k) ? CLOSED_COLUMN_LABEL[k] : TABLERO_COLUMN_LABEL[k]);
/** Total filtrado de la columna (no lo cargado). */
export function columnTotal(counts: BoardCountsDTO | undefined, scope: BoardScope, k: ColumnKey): number | undefined {
  if (!counts) return undefined;
  if (isResult(k)) return counts.closed.byResult[k];
  if (k === "recent") return counts.recent;
  return scope === "attention" ? counts.columns[k].attention : counts.columns[k].total;
}

/** Qué contiene cada columna que no es una fase: la regla de corte a la vista, para que nadie tenga que adivinarla. */
const HINT: Partial<Record<ColumnKey, string>> = {
  recent: `Últimos ${RECENT_COMPLETED_DAYS} días`,
  completed: `Hace más de ${RECENT_COMPLETED_DAYS} días`,
  cancelled: "Desde su cancelación",
  rejected: "Desde su rechazo",
  closed_unmerged: "No cuenta como completada",
};
const EMPTY: Partial<Record<ColumnKey, string>> = {
  recent: `Ninguna misión completada en los últimos ${RECENT_COMPLETED_DAYS} días.`,
  completed: `Ninguna misión completada hace más de ${RECENT_COMPLETED_DAYS} días.`,
  cancelled: "Ninguna misión cancelada.",
  rejected: "Ninguna misión rechazada.",
  closed_unmerged: "Ninguna PR cerrada sin fusionar.",
};

interface ColumnProps {
  col: ColumnKey;
  scope: BoardScope;
  filters: URLSearchParams;
  counts: BoardCountsDTO | undefined;
  selectedId: string | null;
  /** Móvil: la columna ocupa todo el ancho. */
  full?: boolean;
  /** Escritorio: cuántas columnas se reparten la fila (el ancho se adapta para que quepan todas si hay sitio). */
  of?: number;
  emptyText: string;
}

const COL_GAP = 12;
const COL_MIN = 216;
/** Ancho de columna en escritorio: reparte la fila entre las columnas del ámbito, entre un mínimo legible y el ancho de siempre. */
const colWidth = (of: number, wide: boolean) => `clamp(${COL_MIN}px, calc((100% - ${(of - 1) * COL_GAP}px) / ${of}), ${wide ? 340 : 276}px)`;

/** Una columna: altura según su contenido y carga progresiva («Mostrar más»). Los contadores son el total filtrado. */
export function BoardColumn({ col, scope, filters, counts, selectedId, full, of = 5, emptyText }: ColumnProps) {
  const result = isResult(col);
  const q = useBoardPages(filters, result ? { scope: "closed", result: col } : { scope, column: col });
  const items = q.data?.pages.flatMap((p) => p.items) ?? [];
  const total = columnTotal(counts, scope, col) ?? q.data?.pages[0]?.total ?? 0;
  const remaining = Math.max(0, total - items.length);
  const recent = col === "recent";
  const phase = !result && !recent ? counts?.columns[col] : undefined;
  return (
    <section id={`col-${col}`} aria-label={columnLabel(col)} style={full ? undefined : { width: colWidth(of, result) }}
      className={cn("flex flex-none snap-start flex-col rounded-card bg-sf", recent && "border border-gd/40", full && "w-full")}>
      <header className="shrink-0 px-3 pb-2 pt-3">
        <div className="flex items-baseline justify-between gap-2">
          <h2 className="m-0 flex min-w-0 items-start gap-1.5 text-[15px] font-semibold leading-tight">{recent && <CheckCircle2 className="mt-[1px] h-4 w-4 shrink-0 text-gd" aria-hidden />}<span className="min-w-0">{columnLabel(col)}</span></h2>
          <span className="font-mono text-[13px] text-mu" aria-label={`${total} misiones`}>{total}</span>
        </div>
        {/* Informativo: no limita ni bloquea la ejecución. */}
        <div className="mt-0.5 min-h-[18px] text-[12px] text-mu">
          {HINT[col] ?? (scope === "board" && phase ? [phase.running > 0 && `${phase.running} con agente en curso`, phase.attention > 0 && `${phase.attention} necesitan acción`].filter(Boolean).join(" · ") : null)}
        </div>
      </header>
      <div className="flex flex-col gap-2 px-2 pb-2">
        {q.isLoading && Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-[120px]" />)}
        {q.isError && <div className="px-1 py-3 text-[13px] text-er">No se pudo cargar. Se reintenta solo.</div>}
        {!q.isLoading && !q.isError && items.length === 0 && <div className="px-1 py-3 text-[13px] text-mu">{EMPTY[col] ?? emptyText}</div>}
        {items.map((m) => <BoardCard key={m.id} c={m} selected={selectedId === m.id} />)}
        {q.hasNextPage && (
          <Button variant="secondary" size="sm" className="shrink-0" loading={q.isFetchingNextPage} onClick={() => void q.fetchNextPage()}>
            Mostrar más{remaining > 0 ? ` · ${remaining} restantes` : ""}
          </Button>
        )}
        {items.length > 0 && !q.hasNextPage && total > 0 && <div className="shrink-0 px-1 pb-1 text-center text-[12px] text-mu">Mostrando {items.length} de {total}</div>}
      </div>
    </section>
  );
}

function useHScroll(ref: RefObject<HTMLDivElement | null>) {
  const [s, setS] = useState({ left: false, right: false });
  const measure = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const left = el.scrollLeft > 4, right = el.scrollLeft + el.clientWidth < el.scrollWidth - 4;
    setS((p) => (p.left === left && p.right === right ? p : { left, right })); // idempotente: sin esto, cada medida provocaría otro render
  }, [ref]);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    measure();
    el.addEventListener("scroll", measure, { passive: true });
    const ro = new ResizeObserver(measure);
    const watch = () => { ro.disconnect(); ro.observe(el); Array.from(el.children).forEach((c) => ro.observe(c)); measure(); };
    watch();
    const mo = new MutationObserver(watch); // aparece/desaparece una columna (cambio de ámbito)
    mo.observe(el, { childList: true });
    return () => { el.removeEventListener("scroll", measure); ro.disconnect(); mo.disconnect(); };
  }, [ref, measure]);
  return s;
}

interface BoardProps {
  scope: BoardScope;
  filters: URLSearchParams;
  counts: BoardCountsDTO | undefined;
  selectedId: string | null;
  onOpenClosed: () => void;
  emptyText: string;
}

const n = (v: number | undefined) => v ?? "–";

/** Tablero de escritorio: columnas del ámbito en orden, scroll horizontal con flechas, bordes degradados y salto directo a cada columna. */
export function DesktopBoard({ scope, filters, counts, selectedId, onOpenClosed, emptyText }: BoardProps) {
  const ref = useRef<HTMLDivElement>(null);
  const { left, right } = useHScroll(ref);
  const cols = columnsFor(scope);
  const go = (dir: -1 | 1) => ref.current?.scrollBy({ left: dir * Math.max(240, ref.current.clientWidth * 0.8), behavior: "smooth" });
  const jump = (id: string) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth", inline: "start", block: "nearest" });
  const overflow = left || right;
  return (
    <div className="flex flex-col gap-2">
      {overflow && (
        <nav aria-label="Ir a una columna" className="flex shrink-0 flex-wrap items-center gap-1.5 text-[13px]">
          <span className="text-mu">Ir a:</span>
          {cols.map((c) => (
            <button key={c} onClick={() => jump(`col-${c}`)} className={cn("h-8 cursor-pointer rounded-full border bg-transparent px-3 text-tx hover:bg-hv", c === "recent" ? "border-gd/50" : "border-ln2")}>
              {columnLabel(c)} <span className="font-mono text-[12px] text-mu">{n(columnTotal(counts, scope, c))}</span>
            </button>
          ))}
          {scope === "board" && <button onClick={onOpenClosed} className="h-8 cursor-pointer rounded-full border border-dashed border-ln2 bg-transparent px-3 text-tx hover:bg-hv">Cerradas <span className="font-mono text-[12px] text-mu">{n(counts?.closed.total)}</span></button>}
        </nav>
      )}
      <div className="relative">
        <div ref={ref} tabIndex={0} role="region" aria-label="Columnas del tablero, desplázate en horizontal" className="flex snap-x items-start gap-3 overflow-x-auto overflow-y-hidden pb-2 [scrollbar-width:thin]">
          {cols.map((c) => <BoardColumn key={c} col={c} scope={scope} filters={filters} counts={counts} selectedId={selectedId} of={cols.length} emptyText={emptyText} />)}
        </div>
        {left && <><div className="pointer-events-none absolute inset-y-0 left-0 w-10 bg-gradient-to-r from-bg to-transparent" /><button aria-label="Desplazar a la izquierda" onClick={() => go(-1)} className="absolute left-1 top-1/2 flex h-10 w-10 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full border border-ln2 bg-sf text-tx shadow-lg hover:bg-hv"><ChevronLeft className="h-5 w-5" /></button></>}
        {right && <><div className="pointer-events-none absolute inset-y-0 right-0 w-10 bg-gradient-to-l from-bg to-transparent" /><button aria-label="Desplazar a la derecha" onClick={() => go(1)} className="absolute right-1 top-1/2 flex h-10 w-10 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full border border-ln2 bg-sf text-tx shadow-lg hover:bg-hv"><ChevronRight className="h-5 w-5" /></button></>}
      </div>
    </div>
  );
}

/** Móvil / tablet: pestañas con el contador de cada columna y una sola columna a la vez, a todo el ancho (las tarjetas nunca se encogen). */
export function MobileBoard({ scope, filters, counts, selectedId, col, onCol, onOpenClosed, emptyText }: BoardProps & { col: ColumnKey; onCol: (c: ColumnKey) => void }) {
  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3">
      {/* Las pestañas se reparten en varias líneas en vez de deslizarse: ninguna columna (p. ej. «Completadas recientes») queda fuera de pantalla. */}
      <div role="tablist" aria-label="Columnas" className="flex shrink-0 flex-wrap gap-1.5">
        {columnsFor(scope).map((c) => (
          <button key={c} role="tab" id={`tab-${c}`} aria-selected={col === c} aria-controls="board-panel" onClick={() => onCol(c)}
            className={cn("h-10 shrink-0 cursor-pointer rounded-full border px-3.5 text-[14px] font-medium", col === c ? "border-ac bg-ac text-acx" : cn("bg-transparent text-tx", c === "recent" ? "border-gd/50" : "border-ln2"))}>
            {columnLabel(c)} <span className="font-mono text-[12px] opacity-80">{n(columnTotal(counts, scope, c))}</span>
          </button>
        ))}
        {scope === "board" && <button onClick={onOpenClosed} className="h-10 shrink-0 cursor-pointer rounded-full border border-dashed border-ln2 bg-transparent px-3.5 text-[14px] font-medium text-tx">Cerradas <span className="font-mono text-[12px] text-mu">{n(counts?.closed.total)}</span></button>}
      </div>
      <div role="tabpanel" id="board-panel" aria-labelledby={`tab-${col}`} className="flex min-h-0 flex-1 flex-col">
        <BoardColumn key={col} col={col} scope={scope} filters={filters} counts={counts} selectedId={selectedId} full emptyText={emptyText} />
      </div>
    </div>
  );
}

export const Spinner = () => <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-mu" aria-label="Cargando" /></div>;
